import Core from '../plugin-core-system/core.class.js';
import Focus from '../helper/focus.helper.js';
import Aria from '../helper/aria.helper.js';
import Inert from '../helper/inert.helper.js';
import Scroll from '../helper/scroll.helper.js';
import MenuHelper from '../helper/menu.helper.js';
import Breakpoint from '../helper/breakpoint.helper.js';
import {cfg} from './cs-main-navigation.config.js';

export default class CsMainNavigation extends Core {

  static options = {
    openActiveLevelOnMobile: true,
    maxVisibleMenuLevelOnDesktop: 3
  };

  // ==============================
  // Utility Methods
  // ==============================

  isMobile() {
    return Breakpoint.isMobileByCssVar(cfg.breakpoints.mobileMenu);
  }

  isDesktop() {
    return !this.isMobile();
  }

  resetOnBreakpointSwitch(isDesktop) {
    const nowIsDesktop = this.isDesktop();

    // fix to prevent mobile navigation flicker
    this.applyTransitionFix();

    if (nowIsDesktop && !isDesktop) {
      // switch from mobile to desktop
      this.resetMobileMenu();
      this.updateAriaAttributesForMenuLevels();
      return true;
    }

    if (!nowIsDesktop && isDesktop) {
      // switch from desktop to mobile
      this.resetDesktopSubLevels();
      // reset flyout height
      const flyoutContainer = this.el.querySelector(`${cfg.selectors.menuLink}[aria-expanded="true"] + ${cfg.selectors.flyoutContainer}`);
      if (flyoutContainer) {
        this.setFlyoutHeight(flyoutContainer);
      }
      this.updateAriaAttributesForMenuLevels();
      return false;
    }

    return isDesktop;
  }

  applyTransitionFix() {
    this.el.classList.add(cfg.classes.stopTransition);
    window.clearTimeout(this.transitionTimer);

    this.transitionTimer = window.setTimeout(() => {
      this.el.classList.remove(cfg.classes.stopTransition);
      this.transitionTimer = null;
    }, 100);
  }

  // direction 1 => next, -1 => previous
  focusSiblingItem(currentLink, level, direction = 1) {
    let currentList = currentLink.closest('li')?.parentElement;

    if (this.isDesktop() && currentLink.parentElement.matches(cfg.selectors.flyoutInner)) {
      // desktop menu, close flyout button
      currentList = currentLink.nextElementSibling;
    } else if (this.isMobile() && !currentList) {
      // mobile menu, first level close button
      currentList = currentLink.closest('nav').querySelector('ul');
    }

    const currentNavLayer = MenuHelper.getParentNavigationLayerFromLink(currentLink, false, level);

    let target = Focus.getNextFocusableElement(null, direction > 0, currentLink, currentNavLayer);

    if (!target) {
      if (this.isDesktop() && level === 2 && direction > 0) {
        target = currentList?.previousElementSibling;
      } else if (this.isDesktop() && level > 2 && direction > 0) {
        target = currentList.firstElementChild.nextElementSibling.querySelector('a, button');
      } else if (this.isMobile() && level == 1 && direction > 0) {
        target = currentList.previousElementSibling.querySelector('a, button');
      } else {
        const fallback = direction > 0 ? currentList?.firstElementChild : currentList?.lastElementChild;
        target = fallback?.querySelector('a, button');
      }
    }

    Focus.setFocusToElement(target);
  }

  // ==============================
  // Navigation State Control
  // ==============================

  setActiveLinkToActiveState() {
    const activeLink = MenuHelper.getActiveMenuLink();
    if (!activeLink) return false;

    activeLink.classList.add(cfg.classes.active);
    activeLink.setAttribute('aria-current', 'page');

    // climb up the DOM and mark parent links
    let parentLi = activeLink.closest('li');
    while (parentLi) {
      const parentUl = parentLi.parentElement;
      if (parentUl && parentUl.closest('li')) {
        const parentLink = parentUl.closest('li').querySelector('a');
        if (parentLink) {
          parentLink.classList.add(cfg.classes.active);
        }
        parentLi = parentUl.closest('li'); // weiter nach oben
      } else {
        parentLi = null; // oberste Ebene erreicht
      }
    }
  }

  resetExpandedLinks(callbackPerLink) {
    const expandedLinks = this.el.querySelectorAll(`${cfg.selectors.menuLink}[aria-expanded="true"]`);
    expandedLinks.forEach(link => {
      if (typeof callbackPerLink === 'function') {
        callbackPerLink(link);
      } else {
        Aria.setExpanded(link, false);
      }
    });
  }

  resetMobileMenu() {
    this.resetExpandedLinks((link) => this.toggleMobileLevel(link, false));
  }

  resetDesktopSubLevels() {
    this.resetExpandedLinks();
  }

  setFlyoutHeight(flyoutContainer) {
    if (!flyoutContainer) return;

    // reset flyout height
    const flyout = flyoutContainer.querySelector(cfg.selectors.flyout);
    flyoutContainer.removeAttribute('style');
    flyout?.removeAttribute('style');

    if (!this.isDesktop()) return;

    // set flyout and container height
    const header = document.querySelector(cfg.selectors.header);
    const headerHeight = header?.clientHeight ?? 0;

    const flyoutContainerTargetHeight = window.innerHeight - headerHeight;
    const flyoutContentTargetHeight = flyoutContainer.scrollHeight;
    flyoutContainer.style.height = flyoutContainerTargetHeight + 'px';
    if (flyoutContentTargetHeight > flyoutContainerTargetHeight) {
      flyout.style.height = flyoutContentTargetHeight + 'px';
    }
  }

  updateAriaAttributesForMenuLevels() {
    const linksWithChildren = this.el.querySelectorAll('[data-has-children="true"]');
    linksWithChildren.forEach(link => {
      const level = Number(link.getAttribute(cfg.dataAttrs.menuLevel));
      const isTooDeepOnDesktop = this.isDesktop() && level >= this.options.maxVisibleMenuLevelOnDesktop;

      if (isTooDeepOnDesktop) {
        link.removeAttribute('aria-expanded');
        link.removeAttribute('aria-controls');
        link.classList.remove(cfg.selectors.menuLinkWithChildren.slice(1));
        link.classList.remove(cfg.selectors.menuLinkWithChildrenCss.slice(1));
      } else {
        link.setAttribute('aria-expanded', 'false');

        const uid = link.getAttribute('data-menu-item-uid');
        if (uid) {
          link.setAttribute('aria-controls', `main-menu-list-${uid}`);
        }

        link.classList.add(cfg.selectors.menuLinkWithChildrenCss.slice(1));
        link.classList.add(cfg.selectors.menuLinkWithChildren.slice(1));
      }
    });

    const flyoutContainer = this.el.querySelectorAll(cfg.selectors.flyoutContainer);
    const subLevelLists = this.el.querySelectorAll(`${cfg.selectors.menuList}:not([${cfg.dataAttrs.menuLevel}="1"]):not([${cfg.dataAttrs.menuLevel}="2"])`);

    // add inert for:
    // all submenu layers
    const inertTargets = [
      ...flyoutContainer,
      ...subLevelLists
    ];

    Inert.setInertAttrOnElements(inertTargets);
  }

  markLinksWithChildrenOnce() {
    const links = this.el.querySelectorAll(cfg.selectors.menuLinkWithChildren);
    links.forEach(link => {
      link.setAttribute('data-has-children', 'true');
    });
  }


  // ==============================
  // Flyout & Menu Logic
  // ==============================

  openMobileMenuOnActiveLevel() {
    if (!this.options.openActiveLevelOnMobile || this.isDesktop()) return;

    if (this.openActiveLevel()) return;
  }

  toggleMobileLevel(link, toBeOpened = true) {
    let navigationLayerToClose = null;
    let nextNavigationLayer = null;

    if (toBeOpened) {
      nextNavigationLayer = MenuHelper.getSubNavigationLayerFromLink(link);
      navigationLayerToClose = MenuHelper.getParentNavigationLayerFromLink(link);
    } else {
      nextNavigationLayer = MenuHelper.getParentNavigationLayerFromLink(link);
      navigationLayerToClose = MenuHelper.getSubNavigationLayerFromLink(link);
    }

    Scroll.toggleScrollBehavior(navigationLayerToClose, false);
    Scroll.toggleScrollBehavior(nextNavigationLayer, true);

    const focusTarget = toBeOpened
      ? nextNavigationLayer?.querySelector(cfg.selectors.menuListOverview).nextElementSibling.querySelector(cfg.selectors.menuLink)
      : link;
    Focus.setFocusToElement(focusTarget);

    MenuHelper.toggleInertLevelMobile(link, toBeOpened);

    Aria.setExpanded(link, toBeOpened);
  }

  toggleDesktopSubLevel(link, toBeOpened = true) {
    const activeFlyoutContainer = link.closest(cfg.selectors.flyoutContainer);
    if (!activeFlyoutContainer) return;

    //check if another link is aria-expand=true in level 2
    let expandedMenuLink = activeFlyoutContainer.querySelector(`${cfg.selectors.menuLinkWithChildren}[aria-expanded="true"]`);
    if (expandedMenuLink) {
      const level = Number(link.getAttribute(cfg.dataAttrs.menuLevel));
      MenuHelper.resetExpandedSubLevels(activeFlyoutContainer, level);
    }

    Aria.setExpanded(link, toBeOpened);
    const subNavigationLayer = MenuHelper.getSubNavigationLayerFromLink(link);
    Inert.setInertAttr(subNavigationLayer, !toBeOpened);

    if (toBeOpened) {
      const currentLevel = Number(link.getAttribute(cfg.dataAttrs.menuLevel));
      const nextLevel = currentLevel + 1;
      const firstChildLink = link.nextElementSibling.querySelector(
        `${cfg.selectors.menuLink}[${cfg.dataAttrs.menuLevel}="${nextLevel}"]`
      );
      if (firstChildLink) {
        Focus.setFocusToElement(firstChildLink);
      }
    } else {
      Focus.setFocusToElement(link);
    }

    this.setFlyoutHeight(activeFlyoutContainer);
  }

  openParentLevelsUpTo(activeLink, targetLevel) {
    for (let lvl = 1; lvl < targetLevel; lvl++) {
      const selector = `${cfg.selectors.menuItem}${cfg.levelPrefix}${lvl}`;
      const parentLink = activeLink.closest(selector)?.querySelector(cfg.selectors.menuLink);
      if (parentLink) {
        this.toggleMobileLevel(parentLink);
      }
    }
  }

  openActiveLevel() {
    const activeLink = MenuHelper.getActiveMenuLink();
    if (!activeLink) return false;

    let levelToOpen = Number(activeLink.getAttribute(cfg.dataAttrs.menuLevel));
    // Level 0 is the home screen of the mobile menu, so nothing to do if level < 1
    if (levelToOpen < 1) return false;

    const hasChildren = activeLink.matches(cfg.selectors.menuLinkWithChildren);

    const linkToOpen = hasChildren ? activeLink : activeLink.closest(`${cfg.selectors.menuItem}${cfg.levelPrefix}${levelToOpen - 1}`)?.querySelector(cfg.selectors.menuLink);

    if (!linkToOpen) return false;

    // open the parent levels too
    if (levelToOpen >= 2) {
      this.openParentLevelsUpTo(activeLink, levelToOpen);
    }

    this.toggleMobileLevel(linkToOpen);
    return true;
  }

  // ==============================
  // Event Handling
  // ==============================

  handleClickOnMenuLink() {
    this.el.addEventListener('click', e => {
      const link = e.target.closest(cfg.selectors.menuLinkWithChildren);
      if (!link || !this.el.contains(link)) return;

      const level = Number(link.getAttribute(cfg.dataAttrs.menuLevel));
      const isDesktop = this.isDesktop();
      const isDesktopLevel2 = level === 2;
      const isDesktopLevel3 = level === 3;

      if (isDesktop && (isDesktopLevel2 || (this.options.maxVisibleMenuLevelOnDesktop == 4 && isDesktopLevel3))) {
        e.preventDefault();
        this.toggleDesktopSubLevel(link);
        return;
      }

      if (!isDesktop) {
        e.preventDefault();
        this.toggleMobileLevel(link);
        return;
      }
    });
  }

  handleClickOnBackButton() {
    this.el.addEventListener('click', (e) => {
      const backButton = e.target.closest(cfg.selectors.backButton);
      if (!backButton) return;
      if (this.isDesktop()) return;

      const parentTriggerLink = MenuHelper.getParentTriggerLink(backButton);
      this.toggleMobileLevel(parentTriggerLink, false);
    });
  }

  handleModalEvents() {
    const onModalOpen = (e) => {
      const {modal} = e.detail;

      if (modal && modal.contains(this.el)) {
        this.openMobileMenuOnActiveLevel();
      }
      if (modal && modal.matches(cfg.selectors.flyoutContainer)) {
        this.setFlyoutHeight(modal);
      }
    };

    const onModalClose = (e) => {
      const {modal} = e.detail;

      if (modal && modal.contains(this.el)) {
        this.resetMobileMenu();
      }
      if (modal && modal.matches(cfg.selectors.flyoutContainer)) {
        this.resetDesktopSubLevels();
      }
    };

    const onKeyEventOnTriggerToOpenModal = (e) => {
      if (this.isMobile()) return true;
      const {triggerLink} = e.detail;

      triggerLink.click();
    };

    const onKeyEventOnTriggerToCloseModal = (e) => {
      if (this.isMobile()) return true;

      const {goToMode} = e.detail;
      const {triggerLink} = e.detail;
      const level = Number(triggerLink.getAttribute(cfg.dataAttrs.menuLevel));
      const linkHasChildren = triggerLink.matches(cfg.selectors.menuLinkWithChildren);
      let targetTriggerLink = null;

      if (goToMode == 'next' && linkHasChildren || goToMode == 'next' && level == 1) return true;
      if (goToMode == 'previous' && level != 2)  return true;

      const parentItemLevel1 = triggerLink.closest(`${cfg.selectors.menuItem}${cfg.levelPrefix}1`);
      const parentLinkLevel1 = parentItemLevel1.querySelector(cfg.selectors.menuLink);

      // close active modal
      parentLinkLevel1.click();

      if (goToMode == 'previous') {
        // focus the previous item
        targetTriggerLink = parentItemLevel1.previousElementSibling.querySelector(cfg.selectors.menuLink);
      } else if (goToMode == 'next') {
        // focus the next item
        targetTriggerLink = parentItemLevel1.nextElementSibling.querySelector(cfg.selectors.menuLink);
      }

      if (!targetTriggerLink) {
        targetTriggerLink = parentLinkLevel1;
      }
      Focus.setFocusToElement(targetTriggerLink);
    };

    document.addEventListener('cs-modal-open', onModalOpen);
    document.addEventListener('cs-modal-close', onModalClose);
    document.addEventListener('cs-modal-keyevent-on-trigger-to-open-modal', onKeyEventOnTriggerToOpenModal);
    document.addEventListener('cs-modal-keyevent-on-trigger-to-close-modal', onKeyEventOnTriggerToCloseModal);
  }

  handleResize() {
    let isDesktop = this.isDesktop();

    window.addEventListener('resize', () => {
      isDesktop = this.resetOnBreakpointSwitch(isDesktop);
    });
  }

  handleKeydown() {
    this.el.addEventListener('keydown', (e) => {
      const link = e.target.closest('a, button');
      if (!link || !this.el.contains(link)) return;

      const levelFallback = this.isMobile() ? 1 : 2;

      let level = link.closest('ul') ? Number(link.closest('ul')?.getAttribute(cfg.dataAttrs.menuLevel)) : levelFallback;

      // is desktop and event on flyout close button
      if (this.isDesktop() && link.parentElement.matches(cfg.selectors.flyoutInner)) {
        level = 2;
      }

      const linkHasChildren = link.matches(cfg.selectors.menuLinkWithChildren);
      const isMobile = this.isMobile();
      const isDesktop = !isMobile;
      const maxDesktopLevel = this.options.maxVisibleMenuLevelOnDesktop - 1;

      switch (e.key) {
        case ' ':
        case 'Spacebar':
          // Desktop Level 1 is handled in CsModalManager
          if (level >= 2 && isDesktop || isMobile) {
            e.preventDefault();
            e.stopPropagation();
            link.click();
          }
          break;
        case 'ArrowRight':
          if (level === 1 && isDesktop) {
            e.preventDefault();
            this.focusSiblingItem(link, level, 1);
          } else if (level === 2 && !linkHasChildren && isDesktop) {
            e.preventDefault();
            // will be handled in CsModalManager
          } else if (level <= maxDesktopLevel && linkHasChildren && isDesktop) {
            e.preventDefault();
            this.toggleDesktopSubLevel(link);
          } else if (level > maxDesktopLevel && isDesktop) {
            e.preventDefault();
            // will be handled in CsModalManager
          } else if (isMobile && linkHasChildren) {
            e.preventDefault();
            this.toggleMobileLevel(link);
          } else if (isMobile && !linkHasChildren) {
            // do nothing
            e.preventDefault();
            e.stopPropagation();
          }
          break;
        case 'ArrowLeft':
          if (level === 1 && isDesktop) {
            e.preventDefault();
            this.focusSiblingItem(link, level, -1);
          } else if ((level === 3 || (this.options.maxVisibleMenuLevelOnDesktop == 4 && level === 4)) && isDesktop) {
            e.preventDefault();
            e.stopPropagation();
            const triggerLink = link.closest(cfg.selectors.menuList).previousElementSibling;
            this.toggleDesktopSubLevel(triggerLink, false);
          } else if (level >= 2 && isMobile) {
            e.preventDefault();
            e.stopPropagation();
            const parentTriggerLink = MenuHelper.getParentTriggerLink(link);
            this.toggleMobileLevel(parentTriggerLink, false);
          }
          break;
        case 'ArrowDown':
          if (level == 1 && isDesktop) break;

          e.preventDefault();
          e.stopPropagation();
          this.focusSiblingItem(link, level, 1);
          break;
        case 'ArrowUp':
          if (level == 1 && isDesktop) break;

          e.preventDefault();
          this.focusSiblingItem(link, level, -1);
          break;
        case 'Escape':
          if (level === 3 && isDesktop) {
            e.preventDefault();
            e.stopPropagation();
            const triggerLink = link.closest(cfg.selectors.menuList).previousElementSibling;
            this.toggleDesktopSubLevel(triggerLink, false);
          } else if (level >= 2 && isMobile) {
            e.preventDefault();
            e.stopPropagation();
            const parentTriggerLink = MenuHelper.getParentTriggerLink(link);
            this.toggleMobileLevel(parentTriggerLink, false);
          }
          break;
      }
    });
  }

  // ==============================
  // Init
  // ==============================

  init() {
    if (this.el.classList.contains('js-menu-main-show-fourth-level')) {
      this.options.maxVisibleMenuLevelOnDesktop = 4;
    }

    this.setActiveLinkToActiveState();

    this.handleModalEvents();

    this.handleClickOnMenuLink();
    this.handleClickOnBackButton();

    this.handleKeydown();

    this.handleResize();

    this.markLinksWithChildrenOnce();
    this.updateAriaAttributesForMenuLevels();
  }
}
